<template>
  <!-- Use a div wrapper to ensure the toast is always in the DOM -->
  <div class="toast-wrapper">

    <transition name="toast-fade" appear>
      <div v-if="isVisible" class="toast-container" :class="positionClass" style="display: block !important; opacity: 1 !important; visibility: visible !important;">
        <div class="toast" :class="[`toast-${type}`, customClass, {'toast-with-timer': timer && showProgressBar}]">
          <div class="toast-content">
            <div v-if="showLogo" class="toast-logo">
              <img :src="logoPath" alt="Logo" />
            </div>
            <div v-if="icon" class="toast-icon">
              <font-awesome-icon :icon="iconType" class="animated-icon" />
            </div>
            <div class="toast-message">
              <div v-if="title" class="toast-title" v-html="title"></div>
              <div class="toast-text" v-html="message"></div>
            </div>
          </div>
          <button v-if="showCloseButton" @click="close" class="toast-close-btn">
            <font-awesome-icon icon="times" />
          </button>
          <div v-if="timer && showProgressBar" class="toast-progress-container">
            <div class="toast-timer" v-if="showRemainingTime">{{ formattedRemainingTime }}</div>
            <div class="toast-progress-bar">
              <div class="toast-progress" :style="{ width: `${progress}%` }"></div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "Toast",
  props: {
    title: {
      type: String,
      default: ""
    },
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: "info",
      validator: value => ["info", "success", "warning", "error"].includes(value)
    },
    duration: {
      type: Number,
      default: 3000 // 3 seconds
    },
    position: {
      type: String,
      default: "top-end",
      validator: value => [
        "top", "top-start", "top-end", 
        "bottom", "bottom-start", "bottom-end"
      ].includes(value)
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    showProgressBar: {
      type: Boolean,
      default: true
    },
    icon: {
      type: Boolean,
      default: true
    },
    showLogo: {
      type: Boolean,
      default: true
    },
    logoPath: {
      type: String,
      default: "/favicon.ico"
    },
    customClass: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isVisible: false,
      progress: 100,
      timer: null,
      startTime: null,
      remainingTime: null,
      isPaused: false
    };
  },
  computed: {
    positionClass() {
      return `toast-${this.position}`;
    },
    iconType() {
      switch (this.type) {
        case "success":
          return "check-circle";
        case "warning":
          return "exclamation-triangle";
        case "error":
          return "times-circle";
        default:
          return "info-circle";
      }
    },
    formattedRemainingTime() {
      if (!this.remainingTime) return '';
      // Format the remaining time in seconds with one decimal place
      return (this.remainingTime / 1000).toFixed(1) + 's';
    },
    showRemainingTime() {
      return this.timer && this.duration > 0;
    }
  },
  methods: {
    show() {
      this.isVisible = true;
      this.$emit("open");

      if (this.duration) {
        this.startTimer();
      }

      return new Promise(resolve => {
        this.$once("closed", () => {
          resolve();
        });
      });
    },

    close() {
      this.isVisible = false;
      this.clearTimer();
      this.$emit("close");
      this.$emit("closed");
    },

    startTimer() {
      this.clearTimer();
      this.startTime = Date.now();
      this.remainingTime = this.duration;

      this.timer = setInterval(() => {
        if (!this.isPaused) {
          const elapsed = Date.now() - this.startTime;
          this.remainingTime = Math.max(0, this.duration - elapsed);
          this.progress = (this.remainingTime / this.duration) * 100;

          if (this.remainingTime <= 0) {
            this.close();
          }
        }
      }, 10);
    },

    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    pause() {
      if (this.timer) {
        this.isPaused = true;
      }
    },

    resume() {
      if (this.timer && this.isPaused) {
        this.isPaused = false;
        this.startTime = Date.now() - (this.duration - this.remainingTime);
      }
    }
  },
  mounted() {
    // Force visibility after mounting
    this.$nextTick(() => {
      if (this.isVisible) {
        // Force a style update to ensure visibility
        if (this.$el) {
          this.$el.style.display = 'block';
          this.$el.style.opacity = '1';
          this.$el.style.visibility = 'visible';
        }
      }
    });

    // Add event listeners for mouse enter/leave to pause/resume timer
    if (this.$el) {
      this.$el.addEventListener("mouseenter", this.pause);
      this.$el.addEventListener("mouseleave", this.resume);
    }
  },
  beforeDestroy() {
    this.clearTimer();

    if (this.$el) {
      this.$el.removeEventListener("mouseenter", this.pause);
      this.$el.removeEventListener("mouseleave", this.resume);
    }
  }
};
</script>

<style lang="scss" scoped>
.toast-wrapper {
  position: relative;
  z-index: 9999999; /* Match fallback z-index */
}

.toast-container {
  position: fixed;
  z-index: 9999999; /* Match fallback z-index */
  padding: 10px;
  pointer-events: none;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;

  &.toast-top {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &.toast-top-start {
    top: 0;
    left: 0;
  }

  &.toast-top-end {
    top: 10px; /* Added explicit top value */
    right: 10px; /* Added explicit right value */
  }

  &.toast-bottom {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &.toast-bottom-start {
    bottom: 0;
    left: 0;
  }

  &.toast-bottom-end {
    bottom: 0;
    right: 0;
  }
}

.toast {
  display: flex;
  flex-direction: column;
  min-width: 250px;
  max-width: 350px;
  background-color: #fff;
  color: #333;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 10px;
  pointer-events: auto;
  overflow: hidden;
  opacity: 1 !important; /* Force opacity */
  visibility: visible !important; /* Force visibility */

  &.toast-info {
    border-left: 4px solid #3498db;

    .toast-icon {
      color: #3498db;
    }
  }

  &.toast-success {
    border-left: 4px solid #2ecc71;

    .toast-icon {
      color: #2ecc71;
    }
  }

  &.toast-warning {
    border-left: 4px solid #f39c12;
    background-color: #fff3cd;
    color: #856404;

    .toast-icon {
      color: #f39c12;
    }

    .toast-close-btn {
      color: #856404;

      &:hover {
        color: #533f03;
      }
    }
  }

  &.toast-error {
    border-left: 4px solid #e74c3c;

    .toast-icon {
      color: #e74c3c;
    }
  }
}

.toast-content {
  display: flex;
  padding: 12px 15px;
}

.toast-logo {
  margin-right: 8px;
  display: flex;
  align-items: center;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
}

.toast-icon {
  margin-right: 10px;
  font-size: 20px;
  display: flex;
  align-items: center;
}

.toast-message {
  flex: 1;
}

.toast-title {
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 1rem;
}

.toast-text {
  font-size: 0.9rem;
}

.toast-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
}

.toast-progress-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.toast-timer {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 2px;
  font-weight: bold;
  animation: pulse 1s infinite;
}

.toast-progress-bar {
  height: 6px;
  background-color: rgba(0, 0, 0, 0.1);
  width: 100%;
  border-radius: 3px;
  overflow: hidden;
}

.toast-progress {
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  transition: width 0.1s linear;
  border-radius: 3px;
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
  background-size: 16px 16px;
  animation: progress-bar-stripes 1s linear infinite;
}

// Toast with timer styles
.toast-with-timer {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  animation: subtle-bounce 5s ease infinite;
}

// Animated icon
.animated-icon {
  animation: icon-pulse 2s infinite;
}

// Animations
.toast-fade-enter-active {
  animation: toast-in 0.3s ease-out forwards;
}

.toast-fade-leave-active {
  animation: toast-out 0.3s ease-in forwards;
}

@keyframes toast-in {
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translateY(10px) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes toast-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px) scale(0.8);
  }
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

@keyframes icon-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes subtle-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

@keyframes progress-bar-stripes {
  from {
    background-position: 16px 0;
  }
  to {
    background-position: 0 0;
  }
}

/* Force the toast to be visible when it's supposed to be */
.toast-fade-enter-to {
  opacity: 1 !important;
  transform: translateY(0) !important;
  visibility: visible !important;
}

// Responsive styles
@media (max-width: 480px) {
  .toast {
    min-width: 200px;
    max-width: 300px;
  }

  .toast-title {
    font-size: 0.9rem;
  }

  .toast-text {
    font-size: 0.8rem;
  }
}
</style>
